import { useState } from 'react';
import { getErrorMessage } from '@/lib/platform';

import type { Form } from '@/lib/passes-types';
import { Button } from '@/components/ui/button';

/**
 * The TRV14 xinput additional-details step. The form is hosted by the BPP at
 * `form.url` (cross-origin — it can't be embedded in an iframe, and there is no
 * postMessage contract to read the result), so the only workable flow is: open it in
 * a new tab, let the user fill it there, and have them paste back the confirmation /
 * submission ID the BPP returns. We validate that ID via /passes/forms/submit and
 * thread it into select/init/confirm.
 *
 * NOTE: the ONDC Workbench cert harness can't drive an external form, so this path is
 * validated only against a real (cooperative) preprod BPP — not in automated cert.
 */
export function XInputFormGate({
  form,
  onSubmit,
}: {
  form: Form;
  /** Validate + record the submission id, then advance the wizard. */
  onSubmit: (submissionId: string) => Promise<void>;
}) {
  const [submissionId, setSubmissionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const canOpen = Boolean(form.url);

  function openForm() {
    if (form.url) window.open(form.url, '_blank', 'noopener,noreferrer');
  }

  async function submit() {
    const id = submissionId.trim();
    if (!id) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(id);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="wb-card wb-card--pad wb-col wb-gap-12">
      <h2 className="wb-h2">A few details first</h2>
      <p className="wb-sub">
        This venue needs some additional details before booking. Open the form, fill it in, then
        enter the confirmation ID it gives you.
      </p>

      {canOpen ? (
        <div>
          <Button variant="secondary" size="sm" icon="arrowRight" onClick={openForm}>
            Open the form
          </Button>
        </div>
      ) : (
        <p className="wb-small" style={{ color: 'var(--danger)' }}>
          This venue requires a form we can&rsquo;t open right now. Please try another experience.
        </p>
      )}

      {canOpen && (
        <>
          <div>
            <label className="wb-label" htmlFor="submission-id">
              Confirmation ID
            </label>
            <input
              id="submission-id"
              value={submissionId}
              onChange={(e) => setSubmissionId(e.target.value)}
              placeholder="Paste the ID from the form"
              className="wb-field"
            />
          </div>
          {error && (
            <p className="wb-small" style={{ color: 'var(--danger)' }}>
              {error}
            </p>
          )}
          <Button
            variant="primary"
            block
            onClick={submit}
            disabled={!submissionId.trim() || submitting}
          >
            {submitting ? 'Checking…' : "I've completed the form"}
          </Button>
        </>
      )}
    </div>
  );
}
